import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Calendar, ClipboardList, Video, FileText, ExternalLink } from 'lucide-react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';

const CALENDLY_URLS = {
  valoracion: 'https://calendly.com/realimenta-naturalvet/cita',
  pack: 'https://calendly.com/realimenta-naturalvet/pack-nutricional-especifico',
};

const serviceOptions = [
  { 
    id: 'valoracion', 
    name: 'Valoración Nutricional Inicial', 
    price: '35€', 
    description: 'Consulta de 45 min para conocer a tu mascota y orientarte sobre el enfoque nutricional más adecuado.',
    url: CALENDLY_URLS.valoracion,
  },
  { 
    id: 'pack', 
    name: 'Pack Nutricional Específico', 
    price: '100€', 
    description: 'Consulta + plan nutricional personalizado completo con menús, cantidades y pautas de transición.',
    url: CALENDLY_URLS.pack,
  },
];

const steps = [
  {
    number: '01',
    title: 'Elige y Reserva',
    description: 'Selecciona el servicio que necesitas, elige fecha y hora, y realiza el pago de forma segura.',
    icon: Calendar,
    isBooking: true,
  },
  {
    number: '02',
    title: 'Cuestionario Inicial',
    description: 'Antes de nuestra sesión, recibirás un formulario detallado para conocer a fondo a tu mascota.',
    icon: ClipboardList,
    details: [
      'Datos básicos: edad, raza, peso, esterilización',
      'Historial de salud y veterinario de referencia',
      'Alimentación actual y preferencias',
      'Rutina diaria y nivel de actividad',
      'Objetivos que quieres conseguir',
    ],
    tip: 'Cuanto más detallado seas, mejor podré preparar tu caso.',
  },
  {
    number: '03',
    title: 'Consulta Online',
    description: 'Nos conectamos por videollamada para conocernos, revisar el cuestionario y profundizar en los aspectos clave.',
    icon: Video,
    details: [
      'Análisis conjunto de la información proporcionada',
      'Preguntas específicas sobre su comportamiento alimentario',
      'Evaluación de prioridades y objetivos realistas',
      'Explicación del enfoque y metodología',
      'Resolución de todas tus dudas',
    ],
    tip: 'La consulta dura 45 minutos. Prepara tus preguntas.',
  },
  {
    number: '04',
    title: 'Plan Personalizado',
    description: 'Si has elegido Pack Nutricional Específico, en unos días recibes un documento completo con el plan nutricional diseñado específicamente para tu caso.',
    icon: FileText,
    details: [
      'Menú con ingredientes y cantidades exactas',
      'Guía de preparación',
      'Pautas de transición día a día',
      'Recomendaciones de suplementos si son necesarios',
    ],
    tip: 'El plan es tuyo para siempre. Podrás consultarlo cuando lo necesites.',
  },
];

export const ComoFuncionaPage = () => {
  return (
    <>
      <SEOHead
        title="Cómo funciona | REALimenta"
        description="Reserva tu consulta de nutrición veterinaria online. Elige servicio, fecha y hora, paga de forma segura y recibe tu plan personalizado."
        canonical="/como-funciona"
      />

      <section className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[{ label: 'Cómo funciona' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mb-16"
          >
            <h1 
              className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A2E22] tracking-tighter mb-6"
              data-testid="como-funciona-h1"
            >
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #3D6B4F 0%, #8AAE7F 100%)' }}
              >
                Cómo funciona
              </span>
              {' '}el proceso
            </h1>
            <p className="text-text-secondary text-lg md:text-xl">
              Un camino sencillo y acompañado, desde la reserva hasta los resultados.
            </p>
          </motion.div>

          {/* Steps */}
          <div className="space-y-8 mb-20">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="grid lg:grid-cols-[200px_1fr] gap-8"
                data-testid={`step-${step.number}`}
              >
                {/* Step indicator */}
                <div className="flex lg:flex-col items-center lg:items-start gap-4">
                  <span className="font-manrope text-6xl font-extrabold text-brand-sage/50">
                    {step.number}
                  </span>
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-[#EDF2EA] flex items-center justify-center">
                    <step.icon className="w-7 h-7 text-brand-primary" />
                  </div>
                </div>

                {/* Step content */}
                <div className="card-brand">
                  <h2 className="font-manrope text-xl md:text-2xl font-bold text-text-primary mb-3">
                    {step.title}
                  </h2>
                  <p className="text-text-secondary mb-6">
                    {step.description}
                  </p>

                  {/* Booking Step - Direct Calendly buttons */}
                  {step.isBooking && (
                    <div className="space-y-4">
                      <p className="text-text-muted text-sm mb-4">
                        Haz clic en el servicio que prefieras para reservar directamente:
                      </p>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        {serviceOptions.map((service) => (
                          <a
                            key={service.id}
                            href={service.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group p-5 bg-white rounded-xl border-2 border-brand-sage/30 hover:border-brand-primary hover:shadow-lg transition-all duration-200 block"
                            data-testid={`booking-btn-${service.id}`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-semibold text-text-primary group-hover:text-brand-primary transition-colors">
                                {service.name}
                              </span>
                              <span className="font-bold text-brand-primary text-lg">
                                {service.price}
                              </span>
                            </div>
                            <p className="text-text-muted text-sm mb-3">
                              {service.description}
                            </p>
                            <div className="flex items-center gap-2 text-brand-primary font-medium text-sm group-hover:gap-3 transition-all">
                              Reservar ahora
                              <ExternalLink className="w-4 h-4" />
                            </div>
                          </a>
                        ))}
                      </div>

                      {/* Security note */}
                      <div className="flex items-center justify-center gap-2 text-text-muted text-xs mt-4 pt-4 border-t border-brand-sage/20">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Pago seguro procesado por Stripe a través de Calendly
                      </div>
                    </div>
                  )}

                  {/* Regular steps with details */}
                  {step.details && (
                    <>
                      <ul className="space-y-2 mb-6">
                        {step.details.map((detail, i) => (
                          <li key={`${step.number}-detail-${i}`} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" />
                            <span className="text-text-secondary text-sm">{detail}</span>
                          </li>
                        ))}
                      </ul>

                      {step.tip && (
                        <div className="p-4 bg-brand-light-yellow/20 rounded-xl">
                          <p className="text-sm text-text-primary">
                            <strong>Tip:</strong> {step.tip}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Pack only indicator */}
                  {step.onlyForPack && (
                    <div className="mt-4 inline-flex items-center gap-2 text-xs bg-brand-primary/10 text-brand-primary px-3 py-1.5 rounded-full">
                      <FileText className="w-3.5 h-3.5" />
                      Solo en Pack Nutricional Específico
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Help section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-text-secondary mb-4">
              ¿Tienes dudas sobre qué servicio elegir?
            </p>
            <Link
              to="/contacto"
              className="text-brand-primary font-semibold hover:underline inline-flex items-center gap-1"
            >
              Escríbeme y te ayudo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ComoFuncionaPage;
